import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck, Clock, CheckCircle, AlertCircle, Navigation,
  TrendingUp, Users, RefreshCw, ChevronDown, User,
  Award, Package, DollarSign, Zap, Radio, Wifi, WifiOff,
  Filter, MapPin,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import RyderProfileModal from "../../components/RyderProfileModal";
import DropdownPortal from "../../components/DropdownPortal";

dayjs.extend(relativeTime);
dayjs.locale("es");

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

const STATUS_META = {
  pendiente:  { label: "Pendiente",        color: "bg-amber-100 text-amber-700",     dot: "bg-amber-400"   },
  confirmado: { label: "Confirmado",       color: "bg-blue-100 text-blue-700",       dot: "bg-blue-500"    },
  asignado:   { label: "Asignado",         color: "bg-violet-100 text-violet-700",   dot: "bg-violet-500"  },
  listo:      { label: "Listo p/ retirar", color: "bg-green-100 text-green-700",     dot: "bg-green-500"   },
  en_camino:  { label: "En camino",        color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  entregado:  { label: "Entregado",        color: "bg-gray-100 text-gray-600",       dot: "bg-gray-400"    },
};

const RYDER_STATUS_META = {
  disponible: { label: "Disponible", color: "bg-emerald-100 text-emerald-700", ring: "ring-emerald-300", dot: "bg-emerald-500" },
  ocupado:    { label: "Ocupado",    color: "bg-amber-100 text-amber-700",     ring: "ring-amber-300",   dot: "bg-amber-400"  },
  offline:    { label: "Offline",    color: "bg-gray-100 text-gray-500",       ring: "ring-gray-200",    dot: "bg-gray-400"   },
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-4`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{label}</p>
        {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </motion.div>
  );
}

function RyderCard({ ryder, selected, onSelect, compact }) {
  const meta = RYDER_STATUS_META[ryder.status] ?? RYDER_STATUS_META.offline;
  const initials = (ryder.user?.name || "?").slice(0, 2).toUpperCase();
  const displayName = ryder.user?.name || "Sin nombre";

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => onSelect(ryder)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition text-sm hover:bg-gray-50
          ${selected?.id === ryder.id ? "bg-indigo-50 ring-1 ring-indigo-300" : "bg-white"}`}
      >
        <div className={`relative w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 bg-gradient-to-br from-indigo-400 to-violet-500`}>
          {initials}
          <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${meta.dot}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 truncate text-xs">{displayName}</p>
          <p className={`text-[10px] font-medium ${ryder.status === 'disponible' ? 'text-emerald-600' : ryder.status === 'ocupado' ? 'text-amber-600' : 'text-gray-400'}`}>
            {meta.label} · {ryder.deliveriesToday} hoy
          </p>
        </div>
      </button>
    );
  }

  return (
    <motion.div
      layout
      className={`relative rounded-2xl border-2 p-4 cursor-pointer transition-all ${
        selected?.id === ryder.id
          ? "border-indigo-400 bg-indigo-50 shadow-md"
          : ryder.status === 'disponible'
            ? "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
            : ryder.status === 'ocupado'
              ? "border-amber-200 bg-amber-50/30 hover:border-amber-400"
              : "border-gray-200 bg-gray-50 opacity-60 hover:opacity-80"
      }`}
      onClick={() => onSelect?.(ryder)}
    >
      <div className="flex items-start gap-3">
        <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold text-white shrink-0
          bg-gradient-to-br from-indigo-400 to-violet-500`}>
          {initials}
          <span className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${meta.dot}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 justify-between">
            <p className="font-bold text-gray-900 text-sm truncate">{displayName}</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${meta.color}`}>{meta.label}</span>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Package size={11} />{ryder.deliveriesToday} hoy</span>
            <span className="flex items-center gap-1"><DollarSign size={11} />${(ryder.earningsToday || 0).toLocaleString()}</span>
            <span className="flex items-center gap-1"><Award size={11} />{Number(ryder.rating || 0).toFixed(1)}★</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function AssignDropdown({ order, ryders, onAssign, loading }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const availableRyders = ryders.filter(r => r.status === 'disponible');

  if (order.ryder) {
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-violet-700 bg-violet-50 px-2.5 py-1.5 rounded-lg">
        <Truck size={12} />
        <span className="truncate max-w-[90px]">{order.ryder.user?.name || "Ryder"}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={loading}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50"
      >
        <Users size={12} />
        Asignar
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <DropdownPortal
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        className="bg-white border border-gray-200 rounded-2xl shadow-xl w-56 overflow-hidden"
      >
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.12 }}
          >
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pt-2.5 pb-1">
              {availableRyders.length === 0 ? "Sin ryders disponibles" : "Asignar ryder"}
            </p>
            {availableRyders.length === 0 && (
              <p className="text-xs text-gray-500 px-3 pb-3">Todos los ryders están ocupados u offline.</p>
            )}
            {availableRyders.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onAssign(order.id, r.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-indigo-50 text-left transition"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                  {(r.name || r.user?.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{r.name || r.user?.name}</p>
                  <p className="text-[10px] text-emerald-600">{r.deliveriesToday} entregas hoy</p>
                </div>
              </button>
            ))}
          </motion.div>
        </AnimatePresence>
      </DropdownPortal>
    </div>
  );
}

function OrderRow({ order, ryders, onAssign, assigning, onReadyForPickup, markingReady, index }) {
  const meta = STATUS_META[order.status] ?? STATUS_META.pendiente;
  const timeAgo = dayjs(order.createdAt).fromNow();
  const waitMinutes = dayjs().diff(dayjs(order.createdAt), "minute");
  const isUrgent = waitMinutes > 20 && ["pendiente", "confirmado"].includes(order.status);
  const itemsLabel = order.items?.length === 1
    ? order.items[0]?.product?.title ?? "1 producto"
    : `${order.items?.length ?? 0} productos`;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50/80 transition
        ${isUrgent ? "bg-red-50/30" : ""}`}
    >
      {/* Queue position */}
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0
        ${isUrgent ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}`}>
        {index + 1}
      </div>

      {/* Order info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-gray-900 text-sm">#{order.orderNumber ?? order.id.slice(-6).toUpperCase()}</span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${meta.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
          {isUrgent && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 flex items-center gap-1">
              <Zap size={9} />¡Urgente!
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1"><User size={10} />{order.user?.name ?? "Cliente"}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Package size={10} />{itemsLabel}</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <Clock size={10} className={isUrgent ? "text-red-500" : ""} />
            <span className={isUrgent ? "text-red-600 font-semibold" : ""}>{timeAgo}</span>
          </span>
          {order.pickupSede && (
            <><span>·</span><span className="flex items-center gap-1"><MapPin size={10} />{order.pickupSede.name}</span></>
          )}
        </div>
      </div>

      {/* Total */}
      <div className="hidden sm:block text-sm font-bold text-gray-800 shrink-0">
        ${Number(order.total ?? 0).toLocaleString()}
      </div>

      {/* Assign action + Listo para retirar */}
      <div className="shrink-0 flex items-center gap-2">
        {order.status === "asignado" && (
          <button
            onClick={() => onReadyForPickup(order.id)}
            disabled={markingReady === order.id}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-xs font-bold transition disabled:opacity-50 whitespace-nowrap shadow-sm"
            title="Marcar pedido como listo para que el domiciliario venga a buscarlo"
          >
            {markingReady === order.id ? (
              <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <CheckCircle size={13} />
            )}
            Listo p/ retirar
          </button>
        )}
        <AssignDropdown
          order={order}
          ryders={ryders}
          onAssign={onAssign}
          loading={assigning === order.id}
        />
      </div>
    </motion.div>
  );
}

function RankingTab({ ryders }) {
  const sorted = [...ryders].sort((a, b) => b.deliveriesToday - a.deliveriesToday);
  return (
    <div className="p-4 space-y-2">
      {sorted.length === 0 && (
        <div className="text-center text-gray-400 text-sm py-8">No hay datos de hoy</div>
      )}
      {sorted.map((r, i) => {
        const meta = RYDER_STATUS_META[r.status] ?? RYDER_STATUS_META.offline;
        const name = r.name || r.user?.name || "Sin nombre";
        return (
          <div key={r.id} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-gray-100">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-black shrink-0
              ${i === 0 ? "bg-amber-100 text-amber-600" : i === 1 ? "bg-gray-100 text-gray-500" : i === 2 ? "bg-orange-100 text-orange-500" : "bg-gray-50 text-gray-400"}`}>
              {i + 1}
            </div>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-gray-900 text-sm truncate">{name}</p>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
              </div>
              <p className="text-xs text-gray-500">{Number(r.rating || 0).toFixed(1)}★ · {r.totalDeliveries ?? 0} totales</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-gray-900">{r.deliveriesToday}</p>
              <p className="text-[10px] text-gray-400">entregas hoy</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CompanyDispatch() {
  const user = getStoredUser();
  const companyId = user?.company?.id ?? user?.companyId;

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [assigning, setAssigning]     = useState(null);
  const [markingReady, setMarkingReady] = useState(null);
  const [activeTab, setActiveTab] = useState("queue"); // queue | ryders | ranking
  const [statusFilter, setStatusFilter] = useState("all");
  const [profileRyder, setProfileRyder] = useState(null);
  const lastFetchRef = useRef(null);

  const fetchQueue = useCallback(async (silent = false) => {
    if (!companyId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await API.get(`/orders/company/${companyId}/queue`);
      const d = res.data?.data ?? res.data;
      setData(d);
      lastFetchRef.current = new Date();
    } catch {
      if (!silent) toast.error("Error al cargar la cola de despacho");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(() => fetchQueue(true), 30000);
    return () => clearInterval(iv);
  }, [fetchQueue]);

  const handleAssign = async (orderId, ryderId) => {
    setAssigning(orderId);
    try {
      await API.patch(`/orders/${orderId}/assign-ryder`, { ryderId });
      toast.success("Ryder asignado correctamente");
      await fetchQueue(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al asignar ryder");
    } finally {
      setAssigning(null);
    }
  };

  const handleReadyForPickup = async (orderId) => {
    setMarkingReady(orderId);
    try {
      await API.patch(`/orders/${orderId}/ready-for-pickup`);
      toast.success("✅ Domiciliario notificado — el pedido está listo");
      await fetchQueue(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al marcar pedido como listo");
    } finally {
      setMarkingReady(null);
    }
  };

  const orders  = data?.orders  ?? [];
  const ryders  = data?.ryders  ?? [];
  const stats   = data?.stats   ?? {};

  const filteredOrders = statusFilter === "all"
    ? orders
    : orders.filter(o => o.status === statusFilter);

  const disponibles = ryders.filter(r => r.status === "disponible").length;
  const ocupados    = ryders.filter(r => r.status === "ocupado").length;

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        No se encontró empresa asociada
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <Navigation size={20} className="text-indigo-600" />
            Centro de Despacho
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {lastFetchRef.current ? `Actualizado ${dayjs(lastFetchRef.current).fromNow()}` : "Cargando..."}
          </p>
        </div>
        <button
          onClick={() => fetchQueue(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition disabled:opacity-50 shadow-sm"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {/* Stats grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 h-20 border border-gray-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={AlertCircle}  label="Pendientes"    value={stats.pendiente    ?? 0} accent="bg-amber-400"   />
          <StatCard icon={CheckCircle}  label="Confirmados"   value={stats.confirmado   ?? 0} accent="bg-blue-500"    />
          <StatCard icon={Users}        label="Asignados"     value={stats.asignado     ?? 0} accent="bg-violet-500"  />
          <StatCard icon={Navigation}   label="En camino"     value={stats.en_camino    ?? 0} accent="bg-indigo-500"  />
          <StatCard icon={Truck}        label="Entregados hoy" value={stats.entregadoHoy ?? 0} accent="bg-emerald-500" />
          <StatCard icon={TrendingUp}   label="Ingresos hoy"  value={`$${(stats.revenueHoy ?? 0).toLocaleString()}`} accent="bg-supernova-500" />
        </div>
      )}

      {/* Main panel */}
      <div className="flex flex-col lg:flex-row gap-5">

        {/* ── Order queue (left) ── */}
        <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col">

          {/* Queue header + filter */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-gray-900 text-sm">Cola FIFO</h2>
              <span className="text-xs bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">
                {filteredOrders.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Filter size={13} className="text-gray-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-300 text-gray-700 bg-white"
              >
                <option value="all">Todos ({orders.length})</option>
                <option value="pendiente">Pendiente ({stats.pendiente ?? 0})</option>
                <option value="confirmado">Confirmado ({stats.confirmado ?? 0})</option>
                <option value="asignado">Asignado ({stats.asignado ?? 0})</option>
                <option value="en_camino">En camino ({stats.en_camino ?? 0})</option>
              </select>
            </div>
          </div>

          {/* Orders list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="space-y-px">
                {Array(4).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 animate-pulse">
                    <div className="w-7 h-7 rounded-lg bg-gray-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-100 rounded w-1/3" />
                      <div className="h-2 bg-gray-100 rounded w-2/3" />
                    </div>
                    <div className="w-16 h-8 bg-gray-100 rounded-lg" />
                  </div>
                ))}
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
                <CheckCircle size={36} className="mb-3 text-gray-200" />
                <p className="font-semibold text-gray-500">Sin pedidos activos</p>
                <p className="text-xs mt-1">Los nuevos pedidos aparecerán aquí en orden de llegada</p>
              </div>
            ) : (
              <div>
                <AnimatePresence initial={false}>
                  {filteredOrders.map((order, i) => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      index={i}
                      ryders={ryders}
                      onAssign={handleAssign}
                      assigning={assigning}
                      onReadyForPickup={handleReadyForPickup}
                      markingReady={markingReady}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="w-full lg:w-80 shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col">

          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {[
              { key: "ryders",  label: "Ryders",  icon: Truck  },
              { key: "ranking", label: "Ranking", icon: Award  },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition border-b-2
                  ${activeTab === key
                    ? "border-indigo-500 text-indigo-700 bg-indigo-50/50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>

          {/* Ryder status summary */}
          {activeTab === "ryders" && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
              <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                <Wifi size={10} />{disponibles} disponibles
              </span>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                <Radio size={10} />{ocupados} ocupados
              </span>
              {ryders.length - disponibles - ocupados > 0 && (
                <span className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  <WifiOff size={10} />{ryders.length - disponibles - ocupados} offline
                </span>
              )}
            </div>
          )}

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-2">
                {Array(4).fill(0).map((_, i) => (
                  <div key={i} className="h-20 bg-gray-50 rounded-2xl animate-pulse border border-gray-100" />
                ))}
              </div>
            ) : activeTab === "ranking" ? (
              <RankingTab ryders={ryders} />
            ) : (
              <div className="p-3 space-y-2">
                {ryders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center text-gray-400">
                    <Truck size={30} className="mb-2 text-gray-200" />
                    <p className="text-sm font-semibold text-gray-500">Sin ryders registrados</p>
                    <p className="text-xs mt-1">Aprueba ryders desde el panel de Ryders</p>
                  </div>
                ) : (
                  <>
                    {ryders.filter(r => r.status === "disponible").length > 0 && (
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">Disponibles</p>
                    )}
                    {ryders.filter(r => r.status === "disponible").map(r => (
                      <RyderCard key={r.id} ryder={r} onSelect={setProfileRyder} />
                    ))}
                    {ryders.filter(r => r.status === "ocupado").length > 0 && (
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 pt-2">Ocupados</p>
                    )}
                    {ryders.filter(r => r.status === "ocupado").map(r => (
                      <RyderCard key={r.id} ryder={r} onSelect={setProfileRyder} />
                    ))}
                    {ryders.filter(r => r.status === "offline").length > 0 && (
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 pt-2">Offline</p>
                    )}
                    {ryders.filter(r => r.status === "offline").map(r => (
                      <RyderCard key={r.id} ryder={r} onSelect={setProfileRyder} />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ryder profile modal */}
      {profileRyder && (
        <RyderProfileModal
          ryder={profileRyder}
          orders={orders}
          onClose={() => setProfileRyder(null)}
        />
      )}
    </div>
  );
}
